import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    entries: [],

    summary: {
        totalDebit: 0,
        totalCredit: 0,
        balance: 0,
        entryCount: 0,
    },

    pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },

    filters: {
        customerId: "",
        websiteUserId: "",
        mobile: "",
        invoiceId: "",
        fromDate: "",
        toDate: "",
        status: "",
    },

    selectedEntry: null,

    loading: false,

    error: "",
};

/*
|--------------------------------------------------------------------------
| NUMBER HELPER
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    return Number.isFinite(
        parsedValue
    )
        ? parsedValue
        : fallback;
};

/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback
) => {
    return (
        error
            ?.response
            ?.data
            ?.message ||
        error
            ?.data
            ?.message ||
        error
            ?.payload
            ?.message ||
        (
            typeof error
                ?.payload ===
            "string"
                ? error.payload
                : ""
        ) ||
        error
            ?.message ||
        fallback
    );
};

/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
|
| Supports both response formats:
|
| 1. API utility returns response.data directly:
|
| {
|   success: true,
|   data: {
|     entries: [],
|     summary: {}
|   },
|   meta: {}
| }
|
| 2. API utility returns complete Axios response:
|
| {
|   data: {
|     success: true,
|     data: {
|       entries: [],
|       summary: {}
|     },
|     meta: {}
|   }
| }
|--------------------------------------------------------------------------
*/

const getResponseEnvelope = (
    response
) => {
    if (
        response
            ?.data &&
        typeof response
            .data ===
            "object" &&
        (
            response
                .data
                .success !==
                undefined ||
            response
                .data
                .status !==
                undefined ||
            response
                .data
                .code !==
                undefined ||
            response
                .data
                .message !==
                undefined
        )
    ) {
        return response.data;
    }

    return (
        response ||
        {}
    );
};

const getResponseData = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    if (
        envelope
            ?.data &&
        typeof envelope
            .data ===
            "object"
    ) {
        return envelope.data;
    }

    return {};
};

const getResponseMeta = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    return (
        envelope
            ?.meta ||
        envelope
            ?.data
            ?.pagination ||
        envelope
            ?.pagination ||
        {}
    );
};

/*
|--------------------------------------------------------------------------
| QUERY PARAM HELPERS
|--------------------------------------------------------------------------
*/

const cleanQueryParams = (
    params = {}
) => {
    const cleanedParams =
        {};

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                    undefined ||
                value ===
                    null ||
                value ===
                    ""
            ) {
                return;
            }

            cleanedParams[key] =
                value;
        }
    );

    return cleanedParams;
};

const createQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        cleanQueryParams(
            params
        )
    ).forEach(
        ([
            key,
            value,
        ]) => {
            query.set(
                key,
                String(value)
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};

/*
|--------------------------------------------------------------------------
| NORMALIZE PAGINATION
|--------------------------------------------------------------------------
*/

const normalizePagination = (
    pagination = {}
) => {
    const page =
        Math.max(
            toNumber(
                pagination
                    ?.page,
                1
            ),
            1
        );

    const limit =
        Math.max(
            toNumber(
                pagination
                    ?.limit,
                50
            ),
            1
        );

    const total =
        Math.max(
            toNumber(
                pagination
                    ?.total,
                0
            ),
            0
        );

    const calculatedTotalPages =
        total > 0
            ? Math.ceil(
                  total /
                      limit
              )
            : 1;

    const totalPages =
        Math.max(
            toNumber(
                pagination
                    ?.totalPages,
                calculatedTotalPages
            ),
            1
        );

    return {
        page,

        limit,

        total,

        totalPages,

        hasNextPage:
            typeof pagination
                ?.hasNextPage ===
            "boolean"
                ? pagination
                      .hasNextPage
                : page <
                  totalPages,

        hasPreviousPage:
            typeof pagination
                ?.hasPreviousPage ===
            "boolean"
                ? pagination
                      .hasPreviousPage
                : page >
                  1,
    };
};

/*
|--------------------------------------------------------------------------
| NORMALIZE SUMMARY
|--------------------------------------------------------------------------
*/

const normalizeSummary = (
    summary = {}
) => {
    return {
        totalDebit:
            toNumber(
                summary
                    ?.totalDebit,
                0
            ),

        totalCredit:
            toNumber(
                summary
                    ?.totalCredit,
                0
            ),

        balance:
            toNumber(
                summary
                    ?.balance,
                0
            ),

        entryCount:
            toNumber(
                summary
                    ?.entryCount,
                0
            ),
    };
};

/*
|--------------------------------------------------------------------------
| NORMALIZE ENTRY
|--------------------------------------------------------------------------
*/

const normalizeLedgerEntry = (
    entry = {}
) => {
    return {
        ...entry,

        debit:
            toNumber(
                entry
                    ?.debit,
                0
            ),

        credit:
            toNumber(
                entry
                    ?.credit,
                0
            ),

        amount:
            toNumber(
                entry
                    ?.amount,
                0
            ),

        openingBalance:
            toNumber(
                entry
                    ?.openingBalance,
                0
            ),

        runningBalance:
            toNumber(
                entry
                    ?.runningBalance,
                0
            ),

        closingBalance:
            toNumber(
                entry
                    ?.closingBalance,
                0
            ),

        isReversal:
            Boolean(
                entry
                    ?.isReversal
            ),

        customer: {
            _id:
                entry
                    ?.customer
                    ?._id ||
                entry
                    ?.customerId ||
                null,

            websiteUser:
                entry
                    ?.customer
                    ?.websiteUser ||
                entry
                    ?.websiteUser ||
                null,

            customerName:
                entry
                    ?.customer
                    ?.customerName ||
                entry
                    ?.customerSnapshot
                    ?.customerName ||
                "",

            mobile:
                entry
                    ?.customer
                    ?.mobile ||
                entry
                    ?.customerSnapshot
                    ?.mobile ||
                "",

            email:
                entry
                    ?.customer
                    ?.email ||
                entry
                    ?.customerSnapshot
                    ?.email ||
                "",

            companyName:
                entry
                    ?.customer
                    ?.companyName ||
                entry
                    ?.customerSnapshot
                    ?.companyName ||
                "",

            gstin:
                entry
                    ?.customer
                    ?.gstin ||
                entry
                    ?.customerSnapshot
                    ?.gstin ||
                "",
        },

        document: {
            _id:
                entry
                    ?.document
                    ?._id ||
                (
                    typeof entry
                        ?.document ===
                    "string"
                        ? entry.document
                        : null
                ),

            documentType:
                entry
                    ?.document
                    ?.documentType ||
                entry
                    ?.documentType ||
                "",

            documentNumber:
                entry
                    ?.document
                    ?.documentNumber ||
                entry
                    ?.documentNumber ||
                "",

            documentDate:
                entry
                    ?.document
                    ?.documentDate ||
                entry
                    ?.documentDate ||
                null,

            dueDate:
                entry
                    ?.document
                    ?.dueDate ||
                entry
                    ?.dueDate ||
                null,

            documentStatus:
                entry
                    ?.document
                    ?.documentStatus ||
                "",

            paymentStatus:
                entry
                    ?.document
                    ?.paymentStatus ||
                "",
        },

        payment: {
            _id:
                entry
                    ?.payment
                    ?._id ||
                (
                    typeof entry
                        ?.payment ===
                    "string"
                        ? entry.payment
                        : null
                ),

            receiptNumber:
                entry
                    ?.payment
                    ?.receiptNumber ||
                entry
                    ?.receiptNumber ||
                "",

            paymentDate:
                entry
                    ?.payment
                    ?.paymentDate ||
                null,

            paymentMethod:
                entry
                    ?.payment
                    ?.paymentMethod ||
                entry
                    ?.paymentMethod ||
                "",

            paymentStatus:
                entry
                    ?.payment
                    ?.paymentStatus ||
                entry
                    ?.payment
                    ?.status ||
                "",

            referenceNumber:
                entry
                    ?.payment
                    ?.referenceNumber ||
                entry
                    ?.referenceNumber ||
                "",
        },
    };
};

/*
|--------------------------------------------------------------------------
| FETCH CUSTOMER LEDGER
|--------------------------------------------------------------------------
*/

export const fetchCustomerLedger =
    createAsyncThunk(
        "customerLedger/fetchLedger",

        async (
            {
                customerId = "",
                websiteUserId = "",
                mobile = "",
                invoiceId = "",
                fromDate = "",
                toDate = "",
                status = "",
                page = 1,
                limit = 50,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !customerId &&
                    !websiteUserId &&
                    !mobile &&
                    !invoiceId
                ) {
                    return rejectWithValue(
                        "Customer ID, website user ID, mobile number or sale invoice ID is required."
                    );
                }

                if (
                    typeof config
                        ?.getCustomerLedger !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Customer ledger API URL is not configured."
                    );
                }

                const queryString =
                    createQueryString({
                        customerId,
                        websiteUserId,
                        mobile,
                        invoiceId,
                        fromDate,
                        toDate,
                        status,
                        page,
                        limit,
                    });

                const url =
                    `${config.getCustomerLedger()}${queryString}`;

                const api =
                    new API();

                const response =
                    await api.get(
                        url
                    );

                const envelope =
                    getResponseEnvelope(
                        response
                    );

                if (
                    envelope
                        ?.success ===
                        false ||
                    envelope
                        ?.status ===
                        "error"
                ) {
                    return rejectWithValue(
                        envelope
                            ?.message ||
                        "Unable to fetch customer ledger."
                    );
                }

                const responseData =
                    getResponseData(
                        response
                    );

                const responseMeta =
                    getResponseMeta(
                        response
                    );

                const rawEntries =
                    Array.isArray(
                        responseData
                            ?.entries
                    )
                        ? responseData
                              .entries
                        : [];

                const entries =
                    rawEntries.map(
                        normalizeLedgerEntry
                    );

                return {
                    entries,

                    summary:
                        normalizeSummary(
                            responseData
                                ?.summary ||
                            {}
                        ),

                    pagination:
                        normalizePagination(
                            responseMeta
                        ),

                    filters: {
                        customerId,
                        websiteUserId,
                        mobile,
                        invoiceId,
                        fromDate,
                        toDate,
                        status,
                    },
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch customer ledger."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const customerLedgerSlice =
    createSlice({
        name:
            "customerLedger",

        initialState,

        reducers: {
            /*
            |--------------------------------------------------------------------------
            | SET FILTERS
            |--------------------------------------------------------------------------
            */

            setCustomerLedgerFilters: (
                state,
                action
            ) => {
                state.filters = {
                    ...state.filters,
                    ...(
                        action
                            ?.payload ||
                        {}
                    ),
                };

                state.pagination.page =
                    1;
            },

            /*
            |--------------------------------------------------------------------------
            | SET PAGE
            |--------------------------------------------------------------------------
            */

            setCustomerLedgerPage: (
                state,
                action
            ) => {
                state.pagination.page =
                    Math.max(
                        Number(
                            action
                                ?.payload
                        ) ||
                            1,
                        1
                    );
            },

            /*
            |--------------------------------------------------------------------------
            | SET LIMIT
            |--------------------------------------------------------------------------
            */

            setCustomerLedgerLimit: (
                state,
                action
            ) => {
                state.pagination.limit =
                    Math.max(
                        Number(
                            action
                                ?.payload
                        ) ||
                            50,
                        1
                    );

                state.pagination.page =
                    1;
            },

            /*
            |--------------------------------------------------------------------------
            | SELECT ENTRY
            |--------------------------------------------------------------------------
            */

            setSelectedCustomerLedgerEntry: (
                state,
                action
            ) => {
                state.selectedEntry =
                    action
                        ?.payload ||
                    null;
            },

            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED ENTRY
            |--------------------------------------------------------------------------
            */

            clearSelectedCustomerLedgerEntry: (
                state
            ) => {
                state.selectedEntry =
                    null;
            },

            /*
            |--------------------------------------------------------------------------
            | CLEAR ERROR
            |--------------------------------------------------------------------------
            */

            clearCustomerLedgerError: (
                state
            ) => {
                state.error =
                    "";
            },

            /*
            |--------------------------------------------------------------------------
            | CLEAR LEDGER DATA
            |--------------------------------------------------------------------------
            */

            clearCustomerLedger: (
                state
            ) => {
                state.entries =
                    [];

                state.summary = {
                    ...initialState.summary,
                };

                state.pagination = {
                    ...initialState.pagination,

                    limit:
                        state.pagination
                            ?.limit ||
                        initialState
                            .pagination
                            .limit,
                };

                state.selectedEntry =
                    null;

                state.loading =
                    false;

                state.error =
                    "";
            },

            /*
            |--------------------------------------------------------------------------
            | RESET COMPLETE STATE
            |--------------------------------------------------------------------------
            */

            resetCustomerLedgerState:
                () => ({
                    ...initialState,

                    entries: [],

                    summary: {
                        ...initialState.summary,
                    },

                    pagination: {
                        ...initialState.pagination,
                    },

                    filters: {
                        ...initialState.filters,
                    },

                    selectedEntry:
                        null,
                }),
        },

        /*
        |--------------------------------------------------------------------------
        | ASYNC REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers: (
            builder
        ) => {
            builder
                /*
                |--------------------------------------------------------------------------
                | FETCH PENDING
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchCustomerLedger
                        .pending,
                    (
                        state
                    ) => {
                        state.loading =
                            true;

                        state.error =
                            "";
                    }
                )

                /*
                |--------------------------------------------------------------------------
                | FETCH SUCCESS
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchCustomerLedger
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading =
                            false;

                        state.error =
                            "";

                        state.entries =
                            Array.isArray(
                                action
                                    ?.payload
                                    ?.entries
                            )
                                ? action
                                      .payload
                                      .entries
                                : [];

                        state.summary = {
                            ...initialState.summary,
                            ...(
                                action
                                    ?.payload
                                    ?.summary ||
                                {}
                            ),
                        };

                        state.pagination = {
                            ...initialState.pagination,
                            ...(
                                action
                                    ?.payload
                                    ?.pagination ||
                                {}
                            ),
                        };

                        state.filters = {
                            ...initialState.filters,
                            ...(
                                action
                                    ?.payload
                                    ?.filters ||
                                {}
                            ),
                        };
                    }
                )

                /*
                |--------------------------------------------------------------------------
                | FETCH FAILED
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchCustomerLedger
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading =
                            false;

                        state.entries =
                            [];

                        state.summary = {
                            ...initialState.summary,
                        };

                        state.error =
                            action
                                ?.payload ||
                            action
                                ?.error
                                ?.message ||
                            "Unable to fetch customer ledger.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setCustomerLedgerFilters,
    setCustomerLedgerPage,
    setCustomerLedgerLimit,
    setSelectedCustomerLedgerEntry,
    clearSelectedCustomerLedgerEntry,
    clearCustomerLedgerError,
    clearCustomerLedger,
    resetCustomerLedgerState,
} =
    customerLedgerSlice.actions;

/*
|--------------------------------------------------------------------------
| BASE SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerSelector =
    (
        state
    ) => {
        return (
            state
                ?.customerLedger ||

            state
                ?.billing
                ?.customerLedger ||

            state
                ?.billingReducer
                ?.customerLedger ||

            initialState
        );
    };
/*
|--------------------------------------------------------------------------
| ENTRIES SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerEntriesSelector =
    (
        state
    ) => {
        const entries =
            customerLedgerSelector(
                state
            ).entries;

        return Array.isArray(
            entries
        )
            ? entries
            : [];
    };

/*
|--------------------------------------------------------------------------
| SUMMARY SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerSummarySelector =
    (
        state
    ) => {
        return (
            customerLedgerSelector(
                state
            ).summary ||
            initialState.summary
        );
    };

/*
|--------------------------------------------------------------------------
| PAGINATION SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerPaginationSelector =
    (
        state
    ) => {
        return (
            customerLedgerSelector(
                state
            ).pagination ||
            initialState.pagination
        );
    };

/*
|--------------------------------------------------------------------------
| FILTERS SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerFiltersSelector =
    (
        state
    ) => {
        return (
            customerLedgerSelector(
                state
            ).filters ||
            initialState.filters
        );
    };

/*
|--------------------------------------------------------------------------
| LOADING SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerLoadingSelector =
    (
        state
    ) => {
        return Boolean(
            customerLedgerSelector(
                state
            ).loading
        );
    };

/*
|--------------------------------------------------------------------------
| ERROR SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerErrorSelector =
    (
        state
    ) => {
        return (
            customerLedgerSelector(
                state
            ).error ||
            ""
        );
    };

/*
|--------------------------------------------------------------------------
| SELECTED ENTRY SELECTOR
|--------------------------------------------------------------------------
*/

export const selectedCustomerLedgerEntrySelector =
    (
        state
    ) => {
        return (
            customerLedgerSelector(
                state
            ).selectedEntry ||
            null
        );
    };

/*
|--------------------------------------------------------------------------
| ACTIVE ENTRIES SELECTOR
|--------------------------------------------------------------------------
*/

export const activeCustomerLedgerEntriesSelector =
    (
        state
    ) => {
        return customerLedgerEntriesSelector(
            state
        ).filter(
            (
                entry
            ) => {
                return (
                    entry
                        ?.status ===
                        "ACTIVE" &&
                    entry
                        ?.isReversal !==
                        true
                );
            }
        );
    };

/*
|--------------------------------------------------------------------------
| REVERSAL ENTRIES SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerReversalEntriesSelector =
    (
        state
    ) => {
        return customerLedgerEntriesSelector(
            state
        ).filter(
            (
                entry
            ) => {
                return (
                    entry
                        ?.isReversal ===
                        true ||
                    entry
                        ?.status ===
                        "REVERSED"
                );
            }
        );
    };

/*
|--------------------------------------------------------------------------
| OUTSTANDING BALANCE SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerOutstandingSelector =
    (
        state
    ) => {
        return toNumber(
            customerLedgerSummarySelector(
                state
            )?.balance,
            0
        );
    };

/*
|--------------------------------------------------------------------------
| TOTAL DEBIT SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerTotalDebitSelector =
    (
        state
    ) => {
        return toNumber(
            customerLedgerSummarySelector(
                state
            )?.totalDebit,
            0
        );
    };

/*
|--------------------------------------------------------------------------
| TOTAL CREDIT SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerTotalCreditSelector =
    (
        state
    ) => {
        return toNumber(
            customerLedgerSummarySelector(
                state
            )?.totalCredit,
            0
        );
    };

/*
|--------------------------------------------------------------------------
| ENTRY COUNT SELECTOR
|--------------------------------------------------------------------------
*/

export const customerLedgerEntryCountSelector =
    (
        state
    ) => {
        return toNumber(
            customerLedgerSummarySelector(
                state
            )?.entryCount,
            0
        );
    };

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default
    customerLedgerSlice.reducer;